import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Grid3x3, Download, Lock, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { endpoints } from "@/lib/api";

const LEVEL_COLOR = {
  None: "bg-slate-100 dark:bg-slate-800 text-slate-400",
  Novice: "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300",
  Intermediate: "bg-blue-300 text-blue-900",
  Proficient: "bg-indigo-500 text-white",
  Expert: "bg-emerald-500 text-white",
};

const LEVEL_ABBR = { None: "—", Novice: "N", Intermediate: "I", Proficient: "P", Expert: "E" };

export default function CompetencyMatrixPage() {
  const [params, setParams] = useSearchParams();
  const [matrix, setMatrix] = useState({ columns: [], rows: [] });
  const [clients, setClients] = useState([]);
  const [clientSlug, setClientSlug] = useState(params.get("client") || "all");
  const [authorized, setAuthorized] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    endpoints.me().then((u) => endpoints.listConsultants().then((cs) => {
      const me = cs.find((c) => c.name === u.name);
      setAuthorized(u.canonical_role === "admin" || u.canonical_role === "staff" || !!me?.is_manager);
    }));
    endpoints.listClients().then(setClients).catch(() => {});
  }, []);

  useEffect(() => {
    if (!authorized) return;
    const p = {}; if (clientSlug !== "all") p.client_slug = clientSlug;
    endpoints.competencyMatrix(p).then(setMatrix).catch(() => {});
    const n = new URLSearchParams(params);
    if (clientSlug === "all") n.delete("client"); else n.set("client", clientSlug);
    setParams(n, { replace: true });
    // eslint-disable-next-line
  }, [authorized, clientSlug]);

  if (authorized === null) return <div className="p-8 text-sm text-slate-500">Loading...</div>;
  if (!authorized) return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto"><div className="glass-card rounded-3xl p-10 text-center">
      <div className="mx-auto h-12 w-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-4"><Lock className="h-5 w-5" /></div>
      <h1 className="font-display text-xl font-extrabold mb-1">Authorized staff only</h1>
    </div></div>
  );

  const csvHref = endpoints.exportCompetencyMatrixCsvUrl(clientSlug !== "all" ? { client_slug: clientSlug } : {});

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1800px] mx-auto">
      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-md shadow-purple-500/25"><Grid3x3 className="h-5 w-5 text-white" /></div>
          <div>
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight">Competency & Training Matrix</h1>
            <p className="text-xs text-slate-500">{matrix.rows.length} employees × {matrix.columns.length} competencies</p>
          </div>
        </div>
        <div className="ml-auto flex gap-2 items-center">
          <Select value={clientSlug} onValueChange={setClientSlug}>
            <SelectTrigger data-testid="matrix-client-filter" className="h-9 w-[220px] text-xs"><Filter className="h-3.5 w-3.5 mr-1 text-slate-400" /><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All clients</SelectItem>{clients.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
          </Select>
          <Button asChild variant="outline" size="sm" data-testid="matrix-export-csv"><a href={csvHref} download><Download className="h-3.5 w-3.5 mr-1" /> Export CSV</a></Button>
        </div>
      </div>

      <div className="glass-card rounded-3xl p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <span>Legend:</span>
          {["None", "Novice", "Intermediate", "Proficient", "Expert"].map((l) => (
            <div key={l} className="flex items-center gap-1.5"><span className={`inline-block h-5 w-5 rounded-md ${LEVEL_COLOR[l]} flex items-center justify-center text-[10px] font-bold`}>{LEVEL_ABBR[l]}</span> {l}</div>
          ))}
          <span className="ml-4">★ = required for role</span>
        </div>
      </div>

      {matrix.rows.length === 0 || matrix.columns.length === 0 ? (
        <div className="glass-card rounded-3xl p-10 text-center text-sm text-slate-500">No data yet.</div>
      ) : (
        <div className="glass-card rounded-3xl overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="competency-matrix-table" className="min-w-full text-xs border-collapse">
              <thead className="bg-slate-50/70 dark:bg-slate-900/60">
                <tr>
                  <th className="sticky left-0 z-10 bg-slate-50/95 dark:bg-slate-900/95 text-left px-3 py-3 min-w-[220px] font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Employee</th>
                  {matrix.columns.map((c) => (
                    <th key={c} className="px-2 py-3 font-semibold text-slate-500 uppercase tracking-wider text-[9px] min-w-[100px] border-l border-slate-100 dark:border-white/5">
                      <div className="[writing-mode:vertical-rl] rotate-180 whitespace-nowrap py-1">{c}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.rows.map((r) => (
                  <tr key={r.employee_id} className="border-t border-slate-100 dark:border-white/5 hover:bg-slate-50/40 dark:hover:bg-slate-900/40">
                    <td className="sticky left-0 z-10 bg-white/95 dark:bg-slate-950/95 px-3 py-2">
                      <button data-testid={`matrix-emp-${r.employee_id}`} onClick={() => navigate(`/employees?client=${r.client_slug}`)} className="text-left">
                        <div className="font-semibold text-xs">{r.employee_name}</div>
                        <div className="text-[10px] text-slate-500">{r.position} · {r.client_name}</div>
                      </button>
                    </td>
                    {r.cells.map((cell, i) => (
                      <td key={i} className="px-2 py-2 text-center border-l border-slate-100 dark:border-white/5">
                        <div className={`inline-flex flex-col items-center gap-0.5`}>
                          <div className={`h-8 w-8 rounded-lg font-bold text-[11px] flex items-center justify-center ${LEVEL_COLOR[cell.level] || LEVEL_COLOR.None} ${cell.required && cell.level === "None" ? "ring-2 ring-rose-400" : ""}`} title={`${cell.competency}: ${cell.level}${cell.required ? " (required)" : ""}`}>
                            {LEVEL_ABBR[cell.level] || "—"}
                          </div>
                          {cell.required && <span className="text-[8px] text-amber-500">★</span>}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
